import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import AutoModeService from '@/services/AutoModeService';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component
export default class AutoModeHotkeyMixin extends Vue {
    protected showAutoModeConfirmModal: boolean = false;
    private isAutoModeNavigating: boolean = false;
    private lastF1PressTime: number = 0;

    /**
     * Handles getting the next Auto Mode item and navigation.
     * Automatically exits Auto Mode if no items are available.
     * @param currentQueueType - Optional current queue type to get next from current position
     * @param currentItemId - Optional current item ID to get next from current position
     * @param specificQueue - Optional specific queue to filter to (null = all queues)
     * @returns true if navigated to next item, false if auto mode exited or error occurred
     */
    protected async handleAutoModeNextItem(
        currentQueueType?: string,
        currentItemId?: string | number,
        specificQueue?: string | null
    ): Promise<boolean> {
        if (!this.$store.autoModeEnabled) {
            return false;
        }

        if (this.isAutoModeNavigating) {
            console.warn('SD-2299: Auto Mode navigation already in progress, skipping duplicate request');
            return false;
        }

        try {
            this.isAutoModeNavigating = true;

            // If no parameters provided (advancing after completion), use saved specific queue
            let queueToUse = specificQueue;
            if (!currentQueueType && !currentItemId && specificQueue === undefined) {
                const savedQueue = AutoModeService.getSavedSpecificQueue();
                queueToUse = savedQueue;
            }

            const response = await AutoModeService.getNextItem(
                currentQueueType,
                currentItemId,
                queueToUse
            );

            this.$store.currentAutoModeQueue = response.queueType || null;

            if (response.itemId && response.routeName) {
                await this.$router.replace({
                    name: response.routeName,
                    params: response.routeParams || {},
                    query: response.routeQuery || {}
                });
                return true;
            } else {
                // No items available - exit Auto Mode gracefully
                this.$store.autoModeEnabled = false;
                this.$store.currentAutoModeQueue = null;
                this.$store.lastAutoModeResponse = null;

                this.$notification(NotificationOptions.notificationOptionsPreset(
                    response.message || 'All items completed! Auto Mode has been exited.',
                    NotificationOptions.NotificationTypes.success
                ));
                return false;
            }
        } catch (error) {
            console.error('Error in Auto Mode handling:', error);
            this.$notification(NotificationOptions.error(error));
            return false;
        } finally {
            this.isAutoModeNavigating = false;
        }
    }

    async handleF1Press(): Promise<void> {
        try {
            const now = Date.now();
            const timeSinceLastPress = now - this.lastF1PressTime;
            const DEBOUNCE_MS = 300;

            if (timeSinceLastPress < DEBOUNCE_MS) {
                console.debug(`SD-2299: F1 press debounced (${timeSinceLastPress}ms since last press)`);
                return;
            }

            this.lastF1PressTime = now;

            if (this.isAutoModeNavigating) {
                console.debug('SD-2299: F1 press ignored - navigation already in progress');
                return;
            }

            // Determine if we should get next from current position or from beginning
            if (this.$store.autoModeEnabled && this.$store.lastAutoModeResponse) {
                // Skip to next item from current position (respects specific queue filter)
                const lastResponse = this.$store.lastAutoModeResponse;
                const savedQueue = AutoModeService.getSavedSpecificQueue();
                await this.handleAutoModeNextItem(
                    lastResponse.queueType,
                    lastResponse.itemId,
                    savedQueue
                );
            } else {
                // Enable auto mode before getting first item
                this.$store.autoModeEnabled = true;
                // Get saved specific queue preference for fresh auto mode entry
                const savedQueue = AutoModeService.getSavedSpecificQueue();
                await this.handleAutoModeNextItem(undefined, undefined, savedQueue);
            }
        } catch (error) {
            console.error('Error in Auto Mode F1 press:', error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    onAutoModeConfirmed(specificQueue?: string | null): void {
        this.showAutoModeConfirmModal = false;
        this.$store.autoModeEnabled = true;
        this.handleAutoModeNextItem(undefined, undefined, specificQueue);
    }

    onAutoModeCancelled(): void {
        this.showAutoModeConfirmModal = false;
    }
}
